import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ConfirmModal from "../../components/common/ConfirmModal";
import SuccessModal from "../../components/common/SuccessModal";
import {
  createCampaign,
  fetchOrganizations,
  fetchProvince,
  fetchDistrict,
  fetchLocalGovernments,
  fetchUserProfile,
  STORAGE_KEYS,
} from "../../services/api";
import Select from "react-select";

const NEPAL_COUNTRY_ID = 258;

const CreateCampaign = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pendingCampaignData, setPendingCampaignData] = useState(null);
  const [bPartnerName, setBPartnerName] = useState("");

  // State for dropdowns
  const [organizations, setOrganizations] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [localGovernments, setLocalGovernments] = useState([]);

  // Selected values
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [selectedProvinceId, setSelectedProvinceId] = useState("");
  const [selectedDistrictId, setSelectedDistrictId] = useState("");
  const [selectedLocalGovernmentId, setSelectedLocalGovernmentId] =
    useState("");

  // Form data
  const [formData, setFormData] = useState({
    Name: "",
    Description: "",
    StartDate: null,
    StartTime: null,
    Phone: "",
    EMail: "",
    Position: "",
    TargetQty: "",
    ward_no: "",
    Address1: "",
  });

  // Load B_PARTNER name from localStorage
  useEffect(() => {
    try {
      const bPartnerDataString = localStorage.getItem(STORAGE_KEYS.B_PARTNER);
      if (bPartnerDataString) {
        const bPartnerData = JSON.parse(bPartnerDataString);
        setBPartnerName(bPartnerData.Name || "");
      }
    } catch (error) {
      console.error("Failed to get B_PARTNER name from localStorage", error);
    }
  }, []);

  // Load user profile data
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        // console.log("Loading user profile...");
        const userProfile = await fetchUserProfile();
        // console.log("User profile loaded:", userProfile);
        if (userProfile) {
          setFormData((prev) => ({
            ...prev,
            Name: userProfile.name || "",
            EMail: userProfile.email || "",
            Phone: userProfile.phone || "",
            Position: userProfile.position || "",
          }));
        }
      } catch (err) {
        console.error("Failed to load user profile:", err);
        setError("Failed to load user profile: " + err.message);
      }
    };
    loadUserProfile();
  }, []);

  // Load organizations
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        // console.log("Loading organizations...");
        const orgs = await fetchOrganizations();
        // console.log("Organizations loaded:", orgs);
        setOrganizations(orgs);
      } catch (err) {
        console.error("Failed to load organizations:", err);
        setError("Failed to load organizations: " + err.message);
      }
    };
    loadOrganizations();
  }, []);

  // Load provinces
  useEffect(() => {
    const loadProvinces = async () => {
      try {
        // console.log("Loading provinces...");
        const provincesData = await fetchProvince(NEPAL_COUNTRY_ID);
        // console.log("Provinces loaded:", provincesData);
        setProvinces(provincesData);
      } catch (err) {
        console.error("Failed to load provinces:", err);
        setError("Failed to load provinces: " + err.message);
      }
    };
    loadProvinces();
  }, []);

  // Load districts when province changes
  useEffect(() => {
    const loadDistricts = async () => {
      if (selectedProvinceId) {
        try {
          const districtsData = await fetchDistrict(selectedProvinceId);
          setDistricts(districtsData);
          setSelectedDistrictId("");
          setSelectedLocalGovernmentId("");
          setLocalGovernments([]);
        } catch (err) {
          console.error("Failed to load districts:", err);
          setError("Failed to load districts: " + err.message);
        }
      } else {
        setDistricts([]);
        setSelectedDistrictId("");
        setSelectedLocalGovernmentId("");
        setLocalGovernments([]);
      }
    };
    loadDistricts();
  }, [selectedProvinceId]);

  // Load local governments when district changes
  useEffect(() => {
    const loadLocalGovernments = async () => {
      if (selectedDistrictId) {
        try {
          const localGovsData = await fetchLocalGovernments(selectedDistrictId);
          setLocalGovernments(localGovsData);
          setSelectedLocalGovernmentId("");
        } catch (err) {
          console.error("Failed to load local governments:", err);
          setError("Failed to load local governments: " + err.message);
        }
      } else {
        setLocalGovernments([]);
        setSelectedLocalGovernmentId("");
      }
    };
    loadLocalGovernments();
  }, [selectedDistrictId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setError(null);

      // Validation
      if (
        !formData.Name ||
        !formData.StartDate ||
        !formData.StartTime ||
        !selectedOrganizationId ||
        !selectedProvinceId ||
        !selectedDistrictId ||
        !selectedLocalGovernmentId
      ) {
        throw new Error(
          "Please fill in all required fields and select organization, province, district, and local government."
        );
      }

      // Date validation
      if (formData.StartDate) {
        const startDate = new Date(formData.StartDate);
        const now = new Date();
        if (startDate < now.setHours(0, 0, 0, 0)) {
          throw new Error("Start date cannot be in the past.");
        }
      }

      // Phone number validation
      if (formData.Phone && formData.Phone.length !== 10) {
        throw new Error("Phone number must be exactly 10 digits.");
      }

      // Get user data from localStorage
      const userDataStr = localStorage.getItem("user_data");
      const userId = localStorage.getItem("user_id");
      const parsedUserData = JSON.parse(userDataStr);

      // Format dates for API - matching React Native implementation
      const formatDateToISO = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "";
        return date.toISOString().split("T")[0]; // Return only YYYY-MM-DD
      };

      // Format time for API - matching React Native implementation
      const formatTimeForAPI = (time) => {
        if (!time) return "";
        const t = new Date(time);
        if (isNaN(t.getTime())) return "";
        const h = t.getHours();
        const m = t.getMinutes();
        const hourStr = String(h).padStart(2, "0");
        const minStr = String(m).padStart(2, "0");
        return `${hourStr}:${minStr}:00`; // Return HH:MM:SS
      };

      // Combine date and time into 'YYYY-MM-DD HH:MM:SS' format like React Native
      const datePart = formatDateToISO(formData.StartDate);
      const timePart = formatTimeForAPI(formData.StartTime);
      const combinedDateTime = `${datePart} ${timePart}`;

      const campaignData = {
        ...formData,
        StartDate: formatDateToISO(formData.StartDate) + "T00:00:00Z", // Keep as date only
        StartTime: combinedDateTime, // Full ISO string like React Native
        TargetQty: parseInt(formData.TargetQty) || 0,
        AD_Org_ID: parseInt(selectedOrganizationId),
        Country: NEPAL_COUNTRY_ID,
        n_province_ID: parseInt(selectedProvinceId),
        n_district_ID: parseInt(selectedDistrictId),
        n_locgov_ID: parseInt(selectedLocalGovernmentId),
        C_BPartner_Location_ID: parsedUserData?.C_BPartner_Location_ID?.id,
        C_Location_ID: parsedUserData?.C_Location_ID?.id,
        C_BPartner_ID: parsedUserData?.C_BPartner_ID?.id,
        AD_User_ID: userId,
        C_Channel_ID: 1000003,
        C_DocType_ID: 1000047,
        DocStatus: "O",
        IsActive: true,
        IsSummary: false,
        Costs: 0,
      };

      // Store campaign data and show confirmation modal
      setPendingCampaignData(campaignData);
      setShowConfirmModal(true);
    } catch (err) {
      setError(err.message);
      console.error("Campaign validation error:", err);
    }
  };

  const handleConfirmCampaign = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("Campaign data:", pendingCampaignData);
      await createCampaign(pendingCampaignData);

      // Show success modal instead of alert
      setShowSuccessModal(true);
    } catch (err) {
      setError(err.message);
      console.error("Campaign creation error:", err);
    } finally {
      setLoading(false);
      setPendingCampaignData(null);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate("/campaigns");
  };

  const handleInputChange = (field, value) => {
    // For ward_no field, ensure it's a number
    if (field === "ward_no" && value !== "") {
      // Remove any non-numeric characters
      value = value.toString().replace(/[^0-9]/g, "");
    }

    // For Phone field, ensure it's numeric and limit to 10 digits
    if (field === "Phone" && value !== "") {
      // Remove any non-numeric characters
      value = value.toString().replace(/[^0-9]/g, "");
      // Limit to 10 digits
      if (value.length > 10) {
        value = value.slice(0, 10);
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Prevent mouse wheel from changing number input values
  const preventWheelChange = (e) => {
    // Prevent the default wheel behavior when the input is focused
    e.target.blur();
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Back Button */}
      <div className="flex items-center mb-4">
        <button
          onClick={() => navigate("/campaigns")}
          className="flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      <div>
        <div className="flex items-center space-x-3 mb-2">
          <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-red-500" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Create Campaign
          </h1>
        </div>
        <p className="text-gray-600 text-sm sm:text-base">
          Create a new blood donation campaign
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Campaign Information Section */}
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              Campaign Information
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Basic details about the campaign
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Organization and Campaign Name Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Chapter *
                </label>
                <Select
                  value={
                    organizations.find(
                      (org) => org.id === selectedOrganizationId
                    )
                      ? {
                          value: selectedOrganizationId,
                          label: organizations.find(
                            (org) => org.id === selectedOrganizationId
                          )?.Name,
                        }
                      : null
                  }
                  onChange={(option) =>
                    setSelectedOrganizationId(option ? option.value : "")
                  }
                  options={organizations.map((org) => ({
                    value: org.id,
                    label: org.Name,
                  }))}
                  placeholder="Select Chapter"
                  classNamePrefix="react-select"
                  isClearable
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campaign Name *
                </label>
                <input
                  type="text"
                  value={bPartnerName}
                  readOnly
                  className="input-field bg-gray-100"
                  placeholder="Campaign name from profile"
                  required
                />
              </div>
            </div>

            {/* Date and Time Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <div className="relative">
                  <DatePicker
                    selected={formData.StartDate}
                    onChange={(date) => handleInputChange("StartDate", date)}
                    minDate={new Date()}
                    dateFormat="MMMM d, yyyy"
                    placeholderText="Select start date"
                    className="input-field w-full"
                    required
                    showPopperArrow={false}
                    popperClassName="datepicker-popper"
                    popperPlacement="bottom-start"
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time *
                </label>
                <div className="relative">
                  <DatePicker
                    selected={formData.StartTime}
                    onChange={(time) => handleInputChange("StartTime", time)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    placeholderText="Select start time"
                    className="input-field w-full"
                    required
                    showPopperArrow={false}
                    popperClassName="timepicker-popper"
                    popperPlacement="bottom-start"
                  />
                  <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Target Quantity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Quantity *
              </label>
              <input
                type="number"
                value={formData.TargetQty}
                onChange={(e) => handleInputChange("TargetQty", e.target.value)}
                onKeyDown={(e) => {
                  // Prevent the up and down arrow keys from incrementing/decrementing
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={preventWheelChange}
                className="input-field"
                placeholder="Enter target quantity"
                min="0"
                step="1"
              />
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              Contact Information
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Contact details for the campaign
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Contact Person and Position Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Person Name *
                </label>
                <input
                  type="text"
                  value={formData.Name}
                  readOnly
                  className="input-field bg-gray-100"
                  placeholder="Campaign name from profile"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position *
                </label>
                <input
                  type="text"
                  value={formData.Position}
                  onChange={(e) =>
                    handleInputChange("Position", e.target.value)
                  }
                  className="input-field"
                  placeholder="Enter position"
                />
              </div>
            </div>
            {/* Phone and Email Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  value={formData.Phone}
                  onChange={(e) => handleInputChange("Phone", e.target.value)}
                  className="input-field"
                  placeholder="Enter phone number"
                  maxLength="10"
                  pattern="[0-9]{10}"
                  title="Please enter exactly 10 digits"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.EMail}
                  onChange={(e) => handleInputChange("EMail", e.target.value)}
                  className="input-field"
                  placeholder="Enter email address"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Location Information Section */}
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              Location Information
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Campaign venue and location details
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Province, District, Local Government Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Province *
                </label>
                <Select
                  value={
                    provinces.find((prov) => prov.id === selectedProvinceId)
                      ? {
                          value: selectedProvinceId,
                          label: provinces.find(
                            (prov) => prov.id === selectedProvinceId
                          )?.name,
                        }
                      : null
                  }
                  onChange={(option) =>
                    setSelectedProvinceId(option ? option.value : "")
                  }
                  options={provinces.map((prov) => ({
                    value: prov.id,
                    label: prov.name,
                  }))}
                  placeholder="Select Province"
                  classNamePrefix="react-select"
                  isClearable
                  isDisabled={provinces.length === 0}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select District *
                </label>
                <Select
                  value={
                    districts.find((dist) => dist.id === selectedDistrictId)
                      ? {
                          value: selectedDistrictId,
                          label: districts.find(
                            (dist) => dist.id === selectedDistrictId
                          )?.name,
                        }
                      : null
                  }
                  onChange={(option) =>
                    setSelectedDistrictId(option ? option.value : "")
                  }
                  options={districts.map((dist) => ({
                    value: dist.id,
                    label: dist.name,
                  }))}
                  placeholder={
                    selectedProvinceId
                      ? "Select District"
                      : "Select Province First"
                  }
                  classNamePrefix="react-select"
                  isClearable
                  isDisabled={!selectedProvinceId || districts.length === 0}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Local Government *
                </label>
                <Select
                  value={
                    localGovernments.find(
                      (lg) => lg.id === selectedLocalGovernmentId
                    )
                      ? {
                          value: selectedLocalGovernmentId,
                          label: localGovernments.find(
                            (lg) => lg.id === selectedLocalGovernmentId
                          )?.name,
                        }
                      : null
                  }
                  onChange={(option) =>
                    setSelectedLocalGovernmentId(option ? option.value : "")
                  }
                  options={localGovernments.map((lg) => ({
                    value: lg.id,
                    label: lg.name,
                  }))}
                  placeholder={
                    selectedDistrictId
                      ? "Select Local Government"
                      : "Select District First"
                  }
                  classNamePrefix="react-select"
                  isClearable
                  isDisabled={
                    !selectedDistrictId || localGovernments.length === 0
                  }
                  required
                />
              </div>
            </div>

            {/* Ward No and Venue Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ward No
                </label>
                <input
                  type="number"
                  value={formData.ward_no}
                  onChange={(e) => handleInputChange("ward_no", e.target.value)}
                  onKeyDown={(e) => {
                    // Prevent the up and down arrow keys from incrementing/decrementing
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  onWheel={preventWheelChange}
                  className="input-field"
                  placeholder="Enter ward number"
                  min="0"
                  step="1"
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Venue
                </label>
                <input
                  type="text"
                  value={formData.Address1}
                  onChange={(e) =>
                    handleInputChange("Address1", e.target.value)
                  }
                  className="input-field"
                  placeholder="Enter venue address"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Description</h2>
            <p className="text-sm text-gray-600 mt-1">
              Additional details about the campaign
            </p>
          </div>
          <div className="p-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Description
              </label>
              <textarea
                value={formData.Description}
                onChange={(e) =>
                  handleInputChange("Description", e.target.value)
                }
                rows={4}
                className="input-field resize-none"
                placeholder="Enter campaign description..."
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={() => navigate("/campaigns")}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </div>
            ) : (
              "Create Campaign"
            )}
          </button>
        </div>
      </form>

      {/* Custom Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmCampaign}
        title="Create Campaign"
        message="Are you sure you want to create this campaign? This action cannot be undone."
        confirmText="Create Campaign"
        cancelText="Cancel"
      />

      {/* Custom Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Success!"
        message="Campaign created successfully! You will be redirected to the campaigns list."
        buttonText="Continue"
      />
    </div>
  );
};

export default CreateCampaign;
